import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstDayOfMonth, lastDayOfMonth } from '@progress/kendo-date-math';
import { saveAs } from '@progress/kendo-file-saver';
import { Observable, Subject, from } from 'rxjs';
import { map, switchMap, concatMap } from 'rxjs/operators';
import { ValidationItem } from '../../shared/validationItem.model';
import { CrbAuditSummary } from './car-repair-billing-audit-summary.model';
import { CarRepairBillingFile } from './car-repair-billing-file.model';
import { CarRepairBillingHeader } from './car-repair-billing-header.model';
import { InvoiceSummary } from './car-repair-billing-invoice-summary.model';
import { CarRepairBillingRawData } from './car-repair-billing-rawdata.model';
import { Responsibility } from './responsibility.model';
import { CarRepairBillingInvoice } from './car-repair-billing-invoice.model';
import { ProgramWorkSpendSummary } from '../program-work/program-work-spend-summary.model';
import { ArInvoiceType } from '../../shared/arInvoiceType.model';
import { CrbProcessResponse } from '../inbox-invoice/crb-process-response.model'

@Injectable({providedIn: 'root'})
export class CarRepairBillingService {

	private url: string;

	constructor(private httpClient: HttpClient) {
		this.url = 'api/CarRepairBilling';
	}

	public getSequentialStreamOfResults(subject: Subject<any[]>) {
		return subject.pipe(
			switchMap(val => from(val).pipe(
				concatMap(x => this.createArs(x))
			))
		);
	}

	public searchInvoices(sentToIntacct: boolean, dateForClosedInvoices: Date): Observable<InvoiceSummary[]> {
		if (sentToIntacct && dateForClosedInvoices) {
			return this.httpClient.get<InvoiceSummary[]>(this.url + '/searchInvoices?sentToIntacct=' + sentToIntacct +
				'&monthClosedInvoices=' + (dateForClosedInvoices.getMonth() + 1) +
				'&yearClosedInvoices=' + dateForClosedInvoices.getFullYear());
		} else {
			return this.httpClient.get<InvoiceSummary[]>(this.url + '/searchInvoices');
		}
	}

	public getAuditInvoice(carRepairBillingFileId: number, invoiceNumber: string, shopCode: string): Observable<CarRepairBillingFile> {
		return this.httpClient.get<CarRepairBillingFile>(this.url + '/getAuditInvoice?carRepairBillingFileId=' +
			carRepairBillingFileId + '&invoiceNumber=' + invoiceNumber + '&shopCode=' + shopCode);
	}

	public searchAuditRecords(dateFileImported: Date): Observable<CrbAuditSummary[]> {
		return this.httpClient.get<CrbAuditSummary[]>(this.url + '/searchAuditRecords?monthDateImported=' +
			(dateFileImported.getMonth() + 1) + '&yearDateImported=' + dateFileImported.getFullYear());
	}

	public searchArInvoicesByRailcar(railcarId: number, billingPeriodStart: Date, billingPeriodEnd: Date): Observable<InvoiceSummary[]> {
		return this.httpClient.get<InvoiceSummary[]>(this.url + '/searchArInvoicesByRailcar?railcarId=' + railcarId
			+ '&billingPeriodStart=' + billingPeriodStart.toLocaleDateString()
			+ '&billingPeriodEnd=' + billingPeriodEnd.toLocaleDateString());
	}

	public searchArInvoicesByCustomer(customerId: string, billingPeriodStart: Date, billingPeriodEnd: Date): Observable<any> {
		return this.httpClient.get<InvoiceSummary[]>(this.url + '/searchArInvoicesByCustomer?customerId=' + customerId
			+ '&billingPeriodStart=' + billingPeriodStart.toLocaleDateString()
			+ '&billingPeriodEnd=' + billingPeriodEnd.toLocaleDateString());
	}
	public searchArInvoicesByLease(leaseId: number, billingPeriodStart: Date, billingPeriodEnd: Date): Observable<any> {
		return this.httpClient.get<InvoiceSummary[]>(this.url + '/searchArInvoicesByLease?leaseId=' + leaseId
			+ '&billingPeriodStart=' + billingPeriodStart.toISOString()
			+ '&billingPeriodEnd=' + billingPeriodEnd.toISOString());
	}

	public searchArInvoices(dateForClosedInvoices: Date): Observable<any> {
		return this.httpClient.get<InvoiceSummary[]>(this.url + '/searchInvoicesForAr?billingPeriod=' +
			dateForClosedInvoices.toLocaleDateString());
	}

	public  applyRepairAiOnDemand(id: number): Observable<CarRepairBillingHeader> {
		return this.httpClient.get<CarRepairBillingHeader>(this.url + '/applyRepairAiOnDemandAsync?id=' + id);
	}

	public  getForProgram(id: number, isNoWork: boolean = false): Observable<ProgramWorkSpendSummary[]> {
		return this.httpClient.get<ProgramWorkSpendSummary[]>(this.url + '/getForProgram?programWorkId=' + id
			+ '&isNoWork=' + isNoWork);
	}

	public  getCarRepairBillingData(id: number): Observable<CarRepairBillingHeader> {
		return this.httpClient.get<CarRepairBillingHeader>(this.url + '/' + id);
	}

	public getPreviousVersion(crbHeader: CarRepairBillingHeader): Observable<CarRepairBillingHeader> {
		return this.httpClient.get<CarRepairBillingHeader>(this.url + '/GetPreviousVersion?crbHeaderId=' + crbHeader.id);
	}

	public GetInvoice(id: number, isScrap = false): Observable<CarRepairBillingInvoice> {
		return this.httpClient.get<CarRepairBillingInvoice>(this.url + '/GetInvoice?carRepairBillingInvoiceId=' + id
			+ '&isScrap=' + isScrap);
	}

	public GetEstimatesTotalByServiceEventIds(serviceEventIds: number[]): Observable<any> {
		return this.httpClient.post<any>(this.url + '/GetEstimatesTotalByServiceEventIds', serviceEventIds);
	}

	public GetPriorEstimateTotalsByServiceEventId(serviceEventId: number): Observable<any> {
		return this.httpClient.get<any>(this.url + '/GetPriorEstimates?serviceEventId=' + serviceEventId);
	}

	public getResponsibilities(): Observable<Responsibility[]> {
		return this.httpClient.get<Responsibility[]>(this.url + '/Responsibilities');
	}

	public createFiveHundred(fiveHundredLookup: any): Observable<any> {
		return this.httpClient.post(this.url + '/export', fiveHundredLookup, { responseType: 'blob' }).pipe(
			map(data => {
				const blob = new Blob([data], { type: 'text/plain' });
				saveAs(blob, '500byte.txt');
			}));
	}

	public createFiveHundredByR360Responsibility(hdrId: number, rail360Responsibility: number): Observable<any> {
		return this.httpClient.get(this.url + '/ExportFiveHundredFileByR360Responsibility?headerId=' +
			hdrId + '&rail360ResponsibilityCode=' + rail360Responsibility, { responseType: 'blob' }).pipe(
			map(data => {
				const blob = new Blob([data], { type: 'text/plain' });
				saveAs(blob, '500byte.txt');
			}));
	}
	public getHistoricalRepairInvoicesByCar(railcarId: number): Observable<any> {
		return this.httpClient.get(this.url + '/GetHistoricalRepairInvoicesByCar?railcarId=' + railcarId);
	}

	public saveCarRepairBillingData(crbHeader: CarRepairBillingHeader, isApproved: boolean): Observable<CarRepairBillingHeader> {
		return this.httpClient.post<CarRepairBillingHeader>(this.url + '?isApproved=' + isApproved, crbHeader);
	}

	public saveCarRepairBillingInvoice(crbHeaders: CarRepairBillingHeader[], id: number): Observable<CarRepairBillingHeader[]> {
		return this.httpClient.post<CarRepairBillingHeader[]>(this.url + '/SaveCarRepairBillingInvoice?carRepairBillingInvoiceId=' + id, crbHeaders);
	}

	public getManyInvoices(carRepairBillingInvoiceIds:number[], isScrap:boolean = false): Observable<CarRepairBillingInvoice[]> {
		return this.httpClient.post<CarRepairBillingInvoice[]>(this.url +'/GetManyInvoices?isScrap=' + isScrap, carRepairBillingInvoiceIds);
	}

	public getLatestEstimateCrbIdByServiceEvent(serviceEventId: number): Observable<number> {
		return this.httpClient.get<number>(this.url + '/getLatestEstimateCrbIdByServiceEvent/' + serviceEventId);
	}

	public getLatestEstimateByServiceEvent(serviceEventId: number): Observable<any> {
		return this.httpClient.get<any>(this.url + '/getLatestEstimateByServiceEvent/' + serviceEventId);
	}

	public sendAccountsPayable(carRepairBillingInvoiceId: number, openPeriodStart: Date, invoiceDate: Date,
			invoiceDueDate: Date, glPostingDate: Date): Observable<any[]> {
		return this.httpClient.get<any[]>(this.url + '/sendAccountsPayable?carRepairBillingInvoiceId=' + carRepairBillingInvoiceId +
			'&openPeriodStart=' + openPeriodStart.toLocaleDateString() + '&invoiceDate=' + invoiceDate.toLocaleDateString() +
			'&dueDate=' + invoiceDueDate.toLocaleDateString() + '&glPostingDate=' + glPostingDate.toLocaleDateString());
	}

	public sendAccountsPayables(invoices: InvoiceSummary[], openPeriodStart: Date, glPostingDate: Date): Observable<any> {
		return this.httpClient.post<any>(this.url + '/sendAccountsPayables?openPeriodStart=' + openPeriodStart.toLocaleDateString() +
		'&glPostingDate=' + glPostingDate.toLocaleDateString(), invoices);
	}

	public reverseAccountsPayable(carRepairBillingInvoiceId: number): Observable<any[]> {
		return this.httpClient.get<any[]>(this.url + '/reverseAccountsPayable?carRepairBillingInvoiceId=' + carRepairBillingInvoiceId);
	}

	public canReverseAccountsPayable(carRepairBillingInvoiceId: number): Observable<boolean> {
		return this.httpClient.get<boolean>(this.url + '/canReverseAccountsPayable?carRepairBillingInvoiceId=' + carRepairBillingInvoiceId);
	}

	public getOpenPeriodStart(): Observable<Date> {
		// to do: cache this for a period of time
		return this.httpClient.get<Date>(this.url + '/GetOpenPeriodStart');
	}

	public getVendorDueDateTerm(shopId: number): Observable<number> {
		return this.httpClient.get<number>(this.url + '/GetVendorDueDateTerm/' + shopId);
	}

	public getPurchaseOrder(invoiceNumber: string, shopId: number): Observable<any> {
		return this.httpClient.get<any>(this.url + '/getPurchaseOrder/?invoiceNumber=' + invoiceNumber + '&shopId=' + shopId);
	}

	public createArPreview(invoices: any[], billingPeriod: Date): Observable<InvoiceSummary[]> {
		return this.httpClient.post<InvoiceSummary[]>(this.url + '/createarpreview', invoices,
		{
			params: {
				'startDate': firstDayOfMonth(billingPeriod).toDateString(),
				'endDate': lastDayOfMonth(billingPeriod).toDateString()
			}
		})
		.pipe(map(x => x.map(y => new InvoiceSummary(y))));
	}

	public createArs(invoices: any): Observable<InvoiceSummary[]> {
		return this.httpClient.post<InvoiceSummary[]>(this.url + '/createars', [invoices])
			.pipe(map(x => x.map(y => new InvoiceSummary(y))));
	}

	public sendInvoices(invoices: any, billingPeriod: Date, invoiceType: ArInvoiceType): Observable<any> {
		return this.httpClient.post(this.url + '/sendInvoices?billingPeriod=' + billingPeriod.toLocaleDateString() +'&invoiceType='+invoiceType.toString(), invoices);
	}

	public saveRawDataRecords(rawDataRows: CarRepairBillingRawData[]): Observable<CarRepairBillingRawData[]> {
		return this.httpClient.post<CarRepairBillingRawData[]>(this.url + '/SaveRawDataRecords', rawDataRows);
	}

	public getRailincFieldNameByIndex(index: number): string {
		switch (true) {
			case index <= 1:
				return 'Record Format';
			case index <= 5:
				return 'Billing/Invoicing Party';
			case index <= 9:
				return 'Billed Party';
			case index <= 13:
				return 'Account Date (YYMM)';
			case index <= 29:
				return 'Invoice Number';
			case index === 30:
				return 'Price Master Currency Indicator';
			case index <= 32:
				return 'Detail Source';
			case index <= 47:
				return 'Document Reference Number';
			case index <= 51:
				return 'Car Initial';
			case index <= 57:
				return 'Car Number';
			case index === 58:
				return 'Kind of Car Symbol';
			case index === 59:
				return 'Load/Empty Indicator';
			case index <= 65:
				return 'Repair Date (YYMMDD)';
			case index <= 71:
				return 'SPLC';
			case index <= 75:
				return 'Repairing Party';
			case index <= 91:
				return 'Repairing Party Invoice Number';
			case index <= 106:
				return 'Repairing Party Document Reference Number';
			case index <= 108:
				return 'Repair Facility Type';
			case index <= 110:
				return 'Location on Car';
			case index === 111:
				return 'Reserved for future CRB use';
			case index === 112:
				return 'Reserved for future CRB use';
			case index <= 116:
				return 'Quantity';
			case index === 117:
				return 'Condition Code';
			case index <= 119:
				return 'Reserved for future CRB use';
			case index <= 123:
				return 'Applied Job Code';
			case index <= 126:
				return 'Applied Qualifier';
			case index <= 128:
				return 'Why Made Code';
			case index <= 130:
				return 'Reserved for future CRB use';
			case index <= 134:
				return 'Removed Job Code';
			case index <= 137:
				return 'Removed Qualifier';
			case index === 138:
				return 'Responsibility Code';
			case index <= 142:
				return 'Defect Card/JIC Party';
			case index <= 148:
				return 'Defect Card/JIC Date (YYMMDD)';
			case index <= 155:
				return 'Labor Charge';
			case index <= 163:
				return 'Material Charge';
			case index === 164:
				return 'Material Sign';
			case index === 165:
				return 'Machine Priceable Indicator';
			case index === 166:
				return 'Wrong Repair Indicator';
			case index <= 194:
				return 'Narrative';
			case index <= 198:
				return 'Narrative/Applied Wheel Date (MMYY)';
			case index <= 200:
				return 'Narrative/Applied Wheel Manufacture Code';
			case index === 201:
				return 'Narrative/Applied Wheel Class Code';
			case index <= 203:
				return 'Narrative/Applied Side Reading';
			case index <= 205:
				return 'Narrative/Applied Finger Reading';
			case index <= 209:
				return 'Narrative/Removed Wheel Date (MMYY)';
			case index <= 211:
				return 'Narrative/Removed Wheel Manufacture Code';
			case index === 212:
				return 'Narrative/Removed Wheel Class Code';
			case index <= 214:
				return 'Narrative/Removed Side Reading';
			case index <= 216:
				return 'Narrative/Removed Finger Reading';
			case index <= 221:
				return 'Labor Rate';
			case index <= 230:
				return 'Expanded SPLC';
			case index <= 243:
				return 'CIF Repairing Party';
			case index <= 256:
				return 'CIF Billing/Invoicing Party';
			case index <= 269:
				return 'CIF Billed Party';
			case index <= 282:
				return 'CIF Defect/JIC Party';
			case index <= 288:
				return 'Repair Facility Arrival Date (YYMMDD)';
			case index <= 293:
				return 'Line Number';
			case index <= 299:
				return 'Railinc Inbound Date Stamp (YYMMDD)';
			case index <= 305:
				return 'Railing Outbound Date Stamp (YYMMDD)';
			case index === 306:
				return 'Resubmitted Invoice Indicator';
			case index <= 322:
				return 'Original Invoice Number';
			case index <= 326:
				return 'Original Acocunt Date (YYMM)';
			case index <= 340:
				return 'AAR Component ID (CID)';
			case index <= 400:
				return 'Reserved for future CRB use';
			case index <= 500:
				return 'Free User Area';
			default:
				return 'Unknown';
		}
	}

	public reprocessFailedCrb(processingErrorId: number, ignorePriorVersion: boolean = false): Observable<CarRepairBillingRawData[]> {
		var apiUrl = this.url + '/ReprocessFailedCrb/?processingErrorId=' + processingErrorId;
		if (ignorePriorVersion) {
			apiUrl += '&ignorePriorVersionLogic=' + ignorePriorVersion;
		}
		return this.httpClient.get<CarRepairBillingRawData[]>(apiUrl);
	}

	public GetCustomerBrc(leaseNumber: string, billingPeriod: Date, rebillingInvNbr: string): Observable<void> {
		return this.httpClient.get<string>(this.url + '/getArCustomerBrc/?rider=' + leaseNumber + '&billingPeriod=' +
			billingPeriod.toLocaleDateString() + '&rebillingInvoice=' + rebillingInvNbr).pipe(
			map(data => {
				const blob = new Blob([data], { type: 'text/plain' });
				saveAs(blob, leaseNumber + '_500B.txt');
			}));
	}

	public GetCustomerArInvoicePdf(invoiceNumber: string, leaseNumber: string, billingPeriod?: Date): Observable<string> {
		if (billingPeriod) {
			return this.httpClient.get<string>(this.url + '/getCustomerArInvoicePdf/?invoiceNumber=' + invoiceNumber +
				'&billingPeriod=' + billingPeriod.toLocaleDateString() + '&leaseNumber=' + leaseNumber);
		} else {
			return this.httpClient.get<string>(this.url + '/getCustomerArInvoicePdf/?invoiceNumber=' + invoiceNumber +
				'&leaseNumber=' + leaseNumber);
		}
	}

	public GetOwnerBrc(ownerCode: string, billingPeriod: Date, rebillingInvNbr: string): Observable<void> {
		return this.httpClient.get<string>(this.url + '/getArOwnerBrc/?ownerCode=' + ownerCode + '&billingPeriod=' +
			billingPeriod.toLocaleDateString() + '&rebillingInvoice=' + rebillingInvNbr).pipe(
			map(data => {
				const blob = new Blob([data], { type: 'text/plain' });
				saveAs(blob, ownerCode + '_500B.txt');
			}));
	}

	public downloadOwnerBrcPdf(id: number): Observable<Blob> {
		return this.httpClient.get<Blob>(this.url + '/getOwnerBrcCardPdf/?id=' + id, { responseType: 'blob' as 'json' })
			.pipe(
				map(data => {
					const blob = new Blob([data], { type: 'application/pdf' });
					return blob;
			}));
	}

	public downloadAllInOneInvoicePdf(type: string, billingPeriod: Date): Observable<Blob> {
		return this.httpClient.get<Blob>(this.url + '/GetArAllInOneInvoicePdfByType/?type=' + type + '&glPostingDate=' + billingPeriod.toLocaleDateString(),
				{ responseType: 'blob' as 'json' })
			.pipe(
				map(data => {
					const blob = new Blob([data], { type: 'application/pdf' });
					return blob;
			}));
	}

	public getArInvoicePdf(type: string, id: number, identifier: string): Observable<string> {
		return this.httpClient.get<string>(this.url + '/getArInvoicePdf/?type=' + type + '&id=' + id + '&identifier=' + identifier);
	}

	public getSeBrcCardPdf(seId: number, billingType: number): Observable<string> {
		return this.httpClient.get<string>(this.url + '/getServiceEventBrcCardPdf/?seId=' + seId + '&billingType=' + billingType);
	}

	public GetScrapArInvoicePdf(arId: number, invoiceType?: ArInvoiceType): Observable<string> {
		return this.httpClient.get<string>(this.url + `/getScrapArInvoicePdf/?arId=${arId}${!!invoiceType ? `&invoiceType=${invoiceType}` : ''}`);
	}

	public getCarRepairBrcPdf(headerIds: number[]): Observable<Blob> {
		return this.httpClient.post<Blob>(this.url + '/getSoldCarBrcPdf/', headerIds,
			{ responseType: 'blob' as 'json' }).pipe(
				map(data => {
					const blob = new Blob([data], { type: 'application/pdf' });
					return blob;
				}));
	}

	public sendMonthlyCrdxCrbFile2Railinc(glPostingDate: Date): Observable<ValidationItem[]> {
		return this.httpClient.post<ValidationItem[]>(this.url + '/sendMonthlyCrdxCrbFile2Railinc?glPostingDate=' + glPostingDate.toLocaleDateString(), null);
	}

	public getShopAccountingId(facilityId: number): Observable<string> {
		return this.httpClient.get<string>(this.url + '/getShopAccountingId?facilityId=' + facilityId);
	}

	public discardProcessingError(errorId: number): Observable<any> {
		return this.httpClient.delete<any>(this.url + '/discardProcessingError?processingErrorId=' + errorId);
	}

	public discardAPInvoice(id: number): Observable<any> {
		return this.httpClient.delete<any>(this.url + `/discardApInvoice/${id}`);
	}

	public setInvoiceWorkingStatus(id: number, isWorked: boolean): Observable<any> {
		return this.httpClient.put(this.url + '/setInvoiceWorkingStatus?id=' + id + '&worked=' + isWorked, null);
	}

	public deleteEstimate(id: number): Observable<any> {
		return this.httpClient.delete<any>(this.url + `/delete/${id}`);
	}

	public searchApInvoices(start: Date, end: Date, invoiceNumber: string) {
		const p: any = { 'start': start.toDateString(), 'end': end.toDateString(), 'invoiceNumber': invoiceNumber };
		return this.httpClient.get<CarRepairBillingInvoice[]>(`${this.url}/searchApInvoices`, { params: p });
	}
	public getCarRepairDetailsByRailcarIdAndCompletionDate(start: Date, end: Date, railcarId: number, shopsOnly: boolean): Observable<CarRepairBillingHeader[]> {
		const params: any = { 'railcarID': railcarId, 'fromDate': start.toDateString(), 'toDate': end.toDateString(), 'filterShops': shopsOnly };
		return this.httpClient.get<CarRepairBillingHeader[]>(`${this.url}/getRepairDetailsByRailcarAndCompletionDate`, { params: params})
	}

	public processInvoiceInboxRepairBillingFile(rawDataRowIds: number[], seId: number, seTypeId: number, ignorePriorVersionLogic: boolean ): Observable<CrbProcessResponse> { 
		return this.httpClient.post<CrbProcessResponse>(this.url + '/processInvoiceInboxRepairBillingFile', rawDataRowIds,
		{
			headers: { 'Content-Type': 'application/JSON' },
			params: seId ? {
				'seTypeId': seTypeId.toString(),
				'ignorePriorVersionLogic': ignorePriorVersionLogic.toString(),
				'seId': seId.toString()
			} : 
			{	'seTypeId': seTypeId.toString(),
				'ignorePriorVersionLogic': ignorePriorVersionLogic.toString()
			}
		});
	}

	public DeleteCarRepairBillingFileandRawDataRows(crbFileId:number): Observable<number> {
		return this.httpClient.post<number>(`${this.url}/DeleteCarRepairBillingFileandRawDataRows?crbFileId=`+ crbFileId, null);
	}

	public deleteRepairInvoiceAndRawDataRows(invoiceNumber:string, facilityId:number, crbFileId:number): Observable<any> {
		return this.httpClient.post<any>(this.url + '/deleteRepairInvoiceAndRawDataRows?invoiceNumber=' + invoiceNumber + '&crbFileId=' + crbFileId + '&facilityId=' + facilityId, null);
	}

	public getCarRepairReportColumns() {
		return [
			{ key: 'locationOnCar', value: 'Loc.'}, 
			{ key: 'quantity', value: 'Qty' }, 
			{ key: 'conditionCode', value: 'CC' }, 
			{ key: 'appliedJobCode', value: 'AJC' }, 
			{ key: 'appliedQualifier', value: 'AQ' }, 
			{ key: 'narrative', value: 'Description of Repairs Made' }, 
			{ key: 'appliedWheelDate', value: 'MOYR'}, 
			{ key: 'appliedWheelManufactureCode', value: 'MFR' }, 
			{ key: 'appliedWheelClassCode', value: 'CLS' }, 
			{ key: 'appliedWheelSideReading', value: 'SR' }, 
			{ key: 'appliedWheelFingerReading', value: 'FR' }, 
			{ key: 'removedWheelDate', value: 'MOYR' }, 
			{ key: 'removedWheelManufactureCode', value: 'MFR' }, 
			{ key: 'removedWheelClassCode', value: 'CLS' }, 
			{ key: 'removedWheelSideReading', value: 'SR' }, 
			{ key: 'removedWheelFingerReading', value: 'FR' }, 
			{ key: 'whyMadeCode', value: 'WM' }, 
			{ key: 'removedJobCode', value: 'RJC' }, 
			{ key: 'removedQualifier', value: 'RQ' }, 
			{ key: 'responsibilityCode', value: 'RESP' }, 
			{ key: 'laborCharge', value: '$Labor' }, 
			{ key: 'materialCharge', value: '$Material' }, 
			{ key: 'totalRepairCharge', value: '$Total' },		]
	}	
}
